/* eslint-disable no-unused-vars */
import React, { useState } from 'react';
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  ScrollView,
  SafeAreaView,
  StatusBar,
  Alert,
  Dimensions,
} from 'react-native';
import { Ionicons, MaterialCommunityIcons, FontAwesome5 } from '@expo/vector-icons';
import useProfile from '../../hooks/useProfile';
import ProfileManagementService from '../../services/ProfileManagementService';
import PROFILE_KEYS from '../../constants/profileKeys';
import { COMMERCE_DISABLED, CommerceUnavailable } from '../../utils/iosCommerce';

// === API CONFIG ===
const API_BASE = 'https://api.jobeng.ai';
const API_KEY = '16a0651c78da416445e9c2946fb72fd9a6e4ce4a71ba610db15251050c3462ef';
const { width, height } = Dimensions.get('window');


const CoursePurchaseScreen = ({ navigation, route }) => {
  if (COMMERCE_DISABLED) return <CommerceUnavailable navigation={navigation} />;
  // ── Profile integration (auto-injected) ──
  const profileCtx = useProfile();
  const { course, courseId } = route.params;
  const [loading, setLoading] = useState(false);
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState('card');

  // Sample course data with fallbacks
  const courseData = {
    id: course?.id || courseId || '1',
    title: course?.title || 'Engineering Course',
    instructor: course?.instructor || 'Dr. Engineering Professor',
    price: course?.price || 79,
    originalPrice: course?.originalPrice || 99,
    discount: course?.discount || 20,
    duration: course?.duration || '12 hours',
    level: course?.level || 'Intermediate',
    color: course?.color || '#0e4e96',
    icon: course?.icon || 'book-open-variant',
    rating: course?.rating || 4.8,
    students: course?.students || 1250,
    features: course?.features || [
      'Lifetime access to course materials',
      'Certificate of completion',
      'Direct instructor support',
      'Mobile and desktop access',
      'Downloadable resources',
    ],
  };

  const paymentMethods = [
    {
      id: 'card',
      name: 'Credit/Debit Card',
      icon: 'credit-card',
      description: 'Visa, Mastercard, American Express'
    },
    {
      id: 'paypal',
      name: 'PayPal',
      icon: 'paypal',
      description: 'Pay with your PayPal account'
    },
    {
      id: 'apple',
      name: 'Apple Pay',
      icon: 'apple',
      description: 'Touch ID or Face ID'
    },
    {
      id: 'google',
      name: 'Google Pay',
      icon: 'google',
      description: 'Pay with Google'
    },
  ];

  const handlePurchase = () => {
    // Real Stripe payment via PaymentScreen + StripePaymentIntegration.
    // (Previous implementation was a 2-second setTimeout fake — never charged.)
    if (!courseData.price || Number(courseData.price) <= 0) {
      Alert.alert('Price unavailable', 'We could not determine the price of this course. Please go back and try again.');
      return;
    }
    const scenario = {
      id:            `course_${courseData.id}`,
      type:          'course',
      category:      'learning',
      name:          courseData.title,
      description:   `Enrollment in ${courseData.title}`,
      amountInPence: Math.round(Number(courseData.price) * 100),
      currency:      'GBP',
      metadata: {
        course_id:      courseData.id,
        course_title:   courseData.title,
        instructor:     courseData.instructor,
        payment_method: selectedPaymentMethod,
        user_id:        profileCtx?.userId || null,
      },
      // Where to go after a successful payment
      successRoute:  'CourseLearning',
      successParams: { course: courseData },
    };
    navigation.navigate('PaymentScreen', { scenario });
  };

  const renderStars = (rating) => {
    const stars = [];
    const fullStars = Math.floor(rating);
    const hasHalfStar = rating % 1 !== 0;

    for (let i = 0; i < fullStars; i++) {
      stars.push(
        <FontAwesome5 key={i} name="star" size={14} color="#fdb913" solid />
      );
    }

    if (hasHalfStar) {
      stars.push(
        <FontAwesome5 key="half" name="star-half-alt" size={14} color="#fdb913" solid />
      );
    }

    const remainingStars = 5 - Math.ceil(rating);
    for (let i = 0; i < remainingStars; i++) {
      stars.push(
        <FontAwesome5 key={`empty-${i}`} name="star" size={14} color="#e0e0e0" />
      );
    }

    return stars;
  };

  return (
    <SafeAreaView style={styles.safeArea}>
      <StatusBar barStyle="dark-content" backgroundColor="#f0f4f8" />
      
      {/* Header */}
      <View style={styles.header}>
        <TouchableOpacity
          onPress={() => navigation.goBack()}
          style={styles.backButton}
        >
          <Ionicons name="arrow-back" size={24} color="#0e4e96" />
        </TouchableOpacity>
        <Text style={styles.headerTitle}>Purchase Course</Text>
        <View style={styles.placeholder} />
      </View>

      <ScrollView style={styles.container} showsVerticalScrollIndicator={false}>
        {/* Course Summary */}
        <View style={styles.courseSummaryCard}>
          <Text style={styles.sectionTitle}>Course Summary</Text>
          
          <View style={styles.courseHeader}>
            <View style={[styles.courseIcon, { backgroundColor: courseData.color }]}>
              <MaterialCommunityIcons name={courseData.icon} size={32} color="#ffffff" />
            </View>
            <View style={styles.courseInfo}>
              <Text style={styles.courseTitle}>{courseData.title}</Text>
              <Text style={styles.courseInstructor}>by {courseData.instructor}</Text>
              
              <View style={styles.ratingContainer}>
                <View style={styles.starsContainer}>
                  {renderStars(courseData.rating)}
                </View>
                <Text style={styles.ratingText}>
                  {courseData.rating} ({courseData.students.toLocaleString()} students)
                </Text>
              </View>
            </View>
          </View>

          <View style={styles.courseDetails}>
            <View style={styles.detailItem}>
              <MaterialCommunityIcons name="clock-outline" size={16} color="#64748B" />
              <Text style={styles.detailText}>{courseData.duration}</Text>
            </View>
            <View style={styles.detailItem}>
              <MaterialCommunityIcons name="signal" size={16} color="#64748B" />
              <Text style={styles.detailText}>{courseData.level}</Text>
            </View>
            <View style={styles.detailItem}>
              <MaterialCommunityIcons name="certificate" size={16} color="#64748B" />
              <Text style={styles.detailText}>Certificate</Text>
            </View>
          </View>
        </View>

        {/* What's Included */}
        <View style={styles.featuresCard}>
          <Text style={styles.sectionTitle}>What's Included</Text>
          {courseData.features.map((feature, index) => (
            <View key={index} style={styles.featureItem}>
              <MaterialCommunityIcons name="check-circle" size={16} color="#4CAF50" />
              <Text style={styles.featureText}>{feature}</Text>
            </View>
          ))}
        </View>

        {/* Pricing */}
        <View style={styles.pricingCard}>
          <Text style={styles.sectionTitle}>Pricing</Text>
          
          <View style={styles.priceContainer}>
            {courseData.originalPrice && courseData.originalPrice > courseData.price && (
              <View style={styles.discountBadge}>
                <Text style={styles.discountText}>{courseData.discount}% OFF</Text>
              </View>
            )}
            
            <View style={styles.priceRow}>
              {courseData.originalPrice && courseData.originalPrice > courseData.price && (
                <Text style={styles.originalPrice}>£{courseData.originalPrice}</Text>
              )}
              <Text style={styles.currentPrice}>£{courseData.price}</Text>
            </View>
            
            <Text style={styles.priceNote}>One-time payment • Lifetime access</Text>
          </View>
        </View>

        {/* Payment Methods */}
        <View style={styles.paymentCard}>
          <Text style={styles.sectionTitle}>Payment Method</Text>
          
          {paymentMethods.map((method) => (
            <TouchableOpacity
              key={method.id}
              style={[
                styles.paymentMethod,
                selectedPaymentMethod === method.id && styles.selectedPaymentMethod
              ]}
              onPress={() => setSelectedPaymentMethod(method.id)}
            >
              <View style={styles.paymentMethodLeft}>
                <View style={styles.paymentMethodIcon}>
                  <FontAwesome5 name={method.icon} size={20} color="#0e4e96" />
                </View>
                <View style={styles.paymentMethodInfo}>
                  <Text style={styles.paymentMethodName}>{method.name}</Text>
                  <Text style={styles.paymentMethodDescription}>{method.description}</Text>
                </View>
              </View>
              <View style={[
                styles.radioButton,
                selectedPaymentMethod === method.id && styles.selectedRadioButton
              ]}>
                {selectedPaymentMethod === method.id && (
                  <View style={styles.radioButtonInner} />
                )}
              </View>
            </TouchableOpacity>
          ))}
        </View>

        {/* Security Notice */}
        <View style={styles.securityCard}>
          <View style={styles.securityHeader}>
            <MaterialCommunityIcons name="shield-check" size={24} color="#4CAF50" />
            <Text style={styles.securityTitle}>Secure Payment</Text>
          </View>
          <Text style={styles.securityText}>
            Your payment information is encrypted and secure. We use industry-standard security measures to protect your data.
          </Text>
        </View>
      </ScrollView>

      {/* Purchase Button */}
      <View style={styles.purchaseContainer}>
        <View style={styles.totalContainer}>
          <Text style={styles.totalLabel}>Total</Text>
          <Text style={styles.totalPrice}>£{courseData.price}</Text>
        </View>
        
        <TouchableOpacity 
          style={[styles.purchaseButton, loading && styles.disabledButton]}
          onPress={handlePurchase}
          disabled={loading}
        >
          {loading ? (
            <Text style={styles.purchaseButtonText}>Processing...</Text>
          ) : (
            <>
              <MaterialCommunityIcons name="lock" size={20} color="#ffffff" />
              <Text style={styles.purchaseButtonText}>Complete Purchase</Text>
            </>
          )}
        </TouchableOpacity>
      </View>
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  safeArea: {
    flex: 1,
    backgroundColor: '#f0f4f8',
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 15,
    paddingVertical: 12,
    backgroundColor: '#fff',
    borderBottomWidth: 1,
    borderBottomColor: '#e0e0e0',
  },
  backButton: {
    padding: 5,
  },
  headerTitle: {
    fontSize: 20,
    fontWeight: 'bold',
    color: '#0e4e96',
  },
  placeholder: {
    width: 34,
  },
  container: {
    flex: 1,
    padding: 15,
  },
  courseSummaryCard: {
    backgroundColor: '#fff',
    borderRadius: 10,
    padding: 20,
    marginBottom: 15,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.22,
    shadowRadius: 2.22,
    elevation: 3,
  },
  sectionTitle: {
    fontSize: 18,
    fontWeight: 'bold',
    color: '#0e4e96',
    marginBottom: 16,
  },
  courseHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 16,
  },
  courseIcon: {
    width: 60,
    height: 60,
    borderRadius: 10,
    justifyContent: 'center',
    alignItems: 'center',
    marginRight: 16,
  },
  courseInfo: {
    flex: 1,
  },
  courseTitle: {
    fontSize: 18,
    fontWeight: 'bold',
    color: '#0e4e96',
    marginBottom: 4,
  },
  courseInstructor: {
    fontSize: 14,
    color: '#64748B',
    marginBottom: 8,
  },
  ratingContainer: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  starsContainer: {
    flexDirection: 'row',
    marginRight: 8,
  },
  ratingText: {
    fontSize: 12,
    color: '#64748B',
  },
  courseDetails: {
    flexDirection: 'row',
    justifyContent: 'space-around',
  },
  detailItem: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  detailText: {
    fontSize: 12,
    color: '#64748B',
    marginLeft: 4,
  },
  featuresCard: {
    backgroundColor: '#fff',
    borderRadius: 10,
    padding: 20,
    marginBottom: 15,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.22,
    shadowRadius: 2.22,
    elevation: 3,
  },
  featureItem: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 12,
  },
  featureText: {
    fontSize: 14,
    color: '#64748B',
    marginLeft: 12,
    flex: 1,
  },
  pricingCard: {
    backgroundColor: '#fff',
    borderRadius: 10,
    padding: 20,
    marginBottom: 15,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.22,
    shadowRadius: 2.22,
    elevation: 3,
  },
  priceContainer: {
    alignItems: 'center',
    position: 'relative',
  },
  discountBadge: {
    backgroundColor: '#4CAF50',
    paddingHorizontal: 12,
    paddingVertical: 4,
    borderRadius: 12,
    marginBottom: 12,
  },
  discountText: {
    color: '#ffffff',
    fontSize: 12,
    fontWeight: 'bold',
  },
  priceRow: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 8,
  },
  originalPrice: {
    fontSize: 18,
    color: '#94A3B8',
    textDecorationLine: 'line-through',
    marginRight: 12,
  },
  currentPrice: {
    fontSize: 32,
    fontWeight: 'bold',
    color: '#0e4e96',
  },
  priceNote: {
    fontSize: 12,
    color: '#64748B',
  },
  paymentCard: {
    backgroundColor: '#fff',
    borderRadius: 10,
    padding: 20,
    marginBottom: 15,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.22,
    shadowRadius: 2.22,
    elevation: 3,
  },
  paymentMethod: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingVertical: 16,
    paddingHorizontal: 16,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: '#e0e0e0',
    marginBottom: 12,
  },
  selectedPaymentMethod: {
    borderColor: '#0e4e96',
    backgroundColor: '#f0f4f8',
  },
  paymentMethodLeft: {
    flexDirection: 'row',
    alignItems: 'center',
    flex: 1,
  },
  paymentMethodIcon: {
    width: 40,
    height: 40,
    borderRadius: 8,
    backgroundColor: '#f0f4f8',
    justifyContent: 'center',
    alignItems: 'center',
    marginRight: 12,
  },
  paymentMethodInfo: {
    flex: 1,
  },
  paymentMethodName: {
    fontSize: 16,
    fontWeight: '500',
    color: '#0e4e96',
    marginBottom: 2,
  },
  paymentMethodDescription: {
    fontSize: 12,
    color: '#64748B',
  },
  radioButton: {
    width: 20,
    height: 20,
    borderRadius: 10,
    borderWidth: 2,
    borderColor: '#e0e0e0',
    justifyContent: 'center',
    alignItems: 'center',
  },
  selectedRadioButton: {
    borderColor: '#0e4e96',
  },
  radioButtonInner: {
    width: 10,
    height: 10,
    borderRadius: 5,
    backgroundColor: '#0e4e96',
  },
  securityCard: {
    backgroundColor: '#f0f9f0',
    borderRadius: 10,
    padding: 16,
    marginBottom: 20,
  },
  securityHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 8,
  },
  securityTitle: {
    fontSize: 16,
    fontWeight: 'bold',
    color: '#4CAF50',
    marginLeft: 8,
  },
  securityText: {
    fontSize: 14,
    color: '#64748B',
    lineHeight: 20,
  },
  purchaseContainer: {
    backgroundColor: '#fff',
    paddingHorizontal: 15,
    paddingVertical: 15,
    borderTopWidth: 1,
    borderTopColor: '#e0e0e0',
  },
  totalContainer: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 15,
  },
  totalLabel: {
    fontSize: 18,
    fontWeight: 'bold',
    color: '#0e4e96',
  },
  totalPrice: {
    fontSize: 24,
    fontWeight: 'bold',
    color: '#0e4e96',
  },
  purchaseButton: {
    backgroundColor: '#0e4e96',
    borderRadius: 10,
    paddingVertical: 16,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
  },
  disabledButton: {
    backgroundColor: '#94A3B8',
  },
  purchaseButtonText: {
    color: '#ffffff',
    fontSize: 18,
    fontWeight: 'bold',
    marginLeft: 8,
  },
});

export default CoursePurchaseScreen;

